import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';
import { chats$ } from './chats$';

/**
 * Share a chat - creates entry in mellow_shared_chats
 * Returns { id, shareToken } on success
 */
export const shareChat = async (chatId) => {
  const user = auth$.user.get();
  if (!user) throw new Error('Not authenticated');

  // Get chat details for metadata
  const chat = chats$.items.get().find(c => c.id === chatId);
  if (!chat) throw new Error('Chat not found');

  const { data, error } = await supabase
    .from('mellow_shared_chats')
    .insert({
      chat_id: chatId,
      user_id: user.id,
      chat_title: chat.title || 'Untitled Chat',
      language_code: chat.language_code,
      initial_mode: chat.initial_mode || null, // Store initial mode for course detection
      share_mode: 'live', // Live mode - messages fetched from original chat
    })
    .select('id, share_token')
    .single();

  if (error) throw error;

  return {
    id: data.id,
    shareToken: data.share_token,
  };
};

/**
 * Get shared chat by token (for public viewing)
 * Uses the view that joins with original chat for live messages
 */
export const getSharedChatByToken = async (shareToken) => {
  if (!shareToken) return null;

  const { data, error } = await supabase
    .from('mellow_shared_chats_with_messages')
    .select('*')
    .eq('share_token', shareToken)
    .eq('is_revoked', false)
    .single();

  if (error) {
    console.error('Failed to fetch shared chat:', error);
    return null;
  }

  return data;
};

/**
 * Increment view count (fire-and-forget)
 */
export const incrementViewCount = async (shareToken) => {
  if (!shareToken) return;

  try {
    await supabase.rpc('increment_mellow_shared_chat_view_count', {
      p_share_token: shareToken,
    });
  } catch (error) {
    console.warn('Failed to increment view count:', error);
  }
};

/**
 * Get existing share for a chat (if already shared)
 */
export const getExistingShare = async (chatId) => {
  const user = auth$.user.get();
  if (!user) return null;

  const { data, error } = await supabase
    .from('mellow_shared_chats')
    .select('id, share_token, is_revoked')
    .eq('chat_id', chatId)
    .eq('user_id', user.id)
    .eq('is_revoked', false)
    .maybeSingle();

  if (error) {
    console.error('Failed to check existing share:', error);
    return null;
  }

  return data;
};

/**
 * Revoke a shared chat (for future use)
 */
export const revokeShare = async (shareToken) => {
  const user = auth$.user.get();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('mellow_shared_chats')
    .update({
      is_revoked: true,
      revoked_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('share_token', shareToken)
    .eq('user_id', user.id);

  if (error) throw error;
};

